import {
  GhostButton,
  ActionList,
  ActionListItem,
  Icon,
  Divider,
  Link,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { ControlProps, UISchemaElement, Labeled } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import { Stack } from "@certn/cascadia";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";

// valid options defined here, can be added to in future
export enum ListItemTypes {
  Document = "document",
}

interface ActionListDisplayItem extends Omit<UISchemaElement, "type">, Labeled {
  type: string;
  options?: {
    listItemType?: string;
    description?: string;
    filename?: string;
    url?: string;
    buttonLabel?: string;
    hideIcon?: boolean;
    style?: Partial<{
      isLink: boolean;
      border: string;
      borderRadius: string;
    }>;
  };
}

export function ActionListDisplay(props: ControlProps) {
  const options = props.uischema.options || {};
  const { t } = useTranslation();
  const isPDF = useContext(RenderContext).isForPdfGeneration;

  const elements = options?.detail.elements;

  function getListItem(element: ActionListDisplayItem, index: number) {
    const listItemType = element.options?.listItemType;
    if (
      !listItemType ||
      !Object.values(ListItemTypes).includes(listItemType as ListItemTypes)
    ) {
      return null;
    }

    if (!element.label) return null;

    if (listItemType === ListItemTypes.Document)
      return getDocumentListItem(element, index);

    return null;
  }

  function getDocumentListItem(element: ActionListDisplayItem, index: number) {
    const hideIcon = element.options?.hideIcon ?? false;
    const style = omit(element.options?.style, ["isLink"]);
    const isLink = element.options?.style?.isLink ?? false;

    let line2 = element.options?.description;
    if (element.options?.filename)
      line2 =
        line2 + "\u00A0\u00A0\u00A0•\u00A0\u00A0" + element.options?.filename;

    return (
      <ActionListItem
        key={index}
        title={element.label}
        children={line2}
        leading={
          hideIcon ? undefined : <Icon name="description" size="space-6" />
        }
        className={
          isEmpty(style)
            ? undefined
            : css({ border: style.border, borderRadius: style.borderRadius })
        }
        trailing={
          element.options?.url &&
          !isPDF &&
          (isLink ? (
            <Link
              textColor="text-weak"
              testID="file-link"
              textStyle="global/body-secondary"
              onPress={() => window.open(element.options?.url, "_blank")}
            >
              {element.options?.buttonLabel || t("View")}
            </Link>
          ) : (
            <GhostButton
              onPress={() => {
                window.open(element.options?.url, "_blank");
              }}
              size="small"
              rightIcon="open_in_new"
              testID="view-document"
              iconSize="space-4"
              iconType="standard"
            >
              {element.options?.buttonLabel || t("View")}
            </GhostButton>
          ))
        }
        alignItems="center"
      />
    );
  }

  const gap = options.gap;

  return (
    <ActionList
      hideBorder={options.hideBorder ?? true}
      children={
        <Stack gap={gap}>
          {elements.map((element: ActionListDisplayItem, index: number) => {
            return (
              <div key={index}>
                {getListItem(element, index)}
                {!gap && <Divider />}
              </div>
            );
          })}
        </Stack>
      }
    />
  );
}

export const ActionListDisplayRenderer =
  withJsonFormsControlProps(ActionListDisplay);
